import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  buildQuickAddSuggestions,
  buildSuggestedDueAt,
  QUICK_ADD_CATEGORY_OPTIONS,
  QUICK_ADD_POSITION_OPTIONS,
  QUICK_ADD_TIMING_OPTIONS,
  QUICK_ADD_URGENCY_OPTIONS,
  QUICK_ADD_WORKSPACE_OPTIONS,
} from './commandCenterSuggestions';

const EMPTY_FORM = {
  title: '',
  detail: '',
  workspace: 'communication',
  category: 'people',
  urgency: 'medium',
  timing: 'this_week',
  position: 'active',
};

function buildSubmitPayload(formState, suggestions) {
  const state = formState.position === 'later' ? 'later' : 'active';
  const useExactTarget = (
    formState.workspace === suggestions.workspace
    && suggestions.targetRoomId
    && suggestions.targetTab
  );

  return {
    title: formState.title.trim(),
    detail: formState.detail.trim(),
    source_type: 'manual',
    state,
    workspace: formState.workspace,
    category: formState.category,
    urgency_score: suggestions.urgencyScore,
    ai_priority_score: suggestions.aiPriorityScore,
    suggested_position: formState.position,
    rank_reason: suggestions.rankReason,
    due_at: suggestions.dueAt,
    target_room_id: useExactTarget ? suggestions.targetRoomId : null,
    target_tab: useExactTarget ? suggestions.targetTab : null,
    target_anchor: useExactTarget ? suggestions.targetAnchor : null,
  };
}

export default function QuickAddActionDialog({
  open,
  creating,
  onOpenChange,
  onSubmit,
}) {
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [touchedFields, setTouchedFields] = useState({});

  const suggestions = useMemo(() => (
    buildQuickAddSuggestions({
      title: formState.title,
      detail: formState.detail,
    })
  ), [formState.detail, formState.title]);

  useEffect(() => {
    if (open) return;
    setFormState(EMPTY_FORM);
    setTouchedFields({});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setFormState((current) => ({
      ...current,
      workspace: touchedFields.workspace ? current.workspace : suggestions.workspace,
      category: touchedFields.category ? current.category : suggestions.category,
      urgency: touchedFields.urgency ? current.urgency : suggestions.urgency,
      timing: touchedFields.timing ? current.timing : suggestions.timing,
      position: touchedFields.position ? current.position : suggestions.position,
    }));
  }, [open, suggestions, touchedFields]);

  const canSave = Boolean(formState.title.trim());

  const updateField = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const updateSuggestedField = (field, value) => {
    setTouchedFields((current) => ({ ...current, [field]: true }));
    updateField(field, value);
  };

  const handleSave = async () => {
    if (!canSave || creating) return;
    const activeSuggestions = buildQuickAddSuggestions({
      title: formState.title,
      detail: formState.detail,
    });

    const mergedSuggestions = {
      ...activeSuggestions,
      urgency: formState.urgency,
      timing: formState.timing,
      position: formState.position,
    };

    const urgencyOption = QUICK_ADD_URGENCY_OPTIONS.find((item) => item.value === formState.urgency) || QUICK_ADD_URGENCY_OPTIONS[2];
    mergedSuggestions.urgencyScore = urgencyOption.score;
    mergedSuggestions.aiPriorityScore = urgencyOption.score + (formState.position === 'top3' ? 10 : formState.position === 'later' ? -15 : 0);
    mergedSuggestions.dueAt = buildSuggestedDueAt(formState.timing);

    const result = await onSubmit?.(buildSubmitPayload(formState, mergedSuggestions));
    if (result) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Quick Add To Priority Board</DialogTitle>
          <DialogDescription>
            Capture the task first, then keep or edit the suggested metadata before it lands in the priority-board queue.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          <div className="grid gap-2">
            <label className="text-sm font-medium text-slate-900 dark:text-white">Title</label>
            <Input
              value={formState.title}
              onChange={(event) => updateField('title', event.target.value)}
              placeholder="What needs to happen?"
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-medium text-slate-900 dark:text-white">Detail</label>
            <Textarea
              value={formState.detail}
              onChange={(event) => updateField('detail', event.target.value)}
              placeholder="Optional context that will help later."
              rows={4}
            />
          </div>

          <div className="rounded-2xl border border-sky-200 bg-sky-50/80 p-4 dark:border-sky-400/15 dark:bg-sky-500/5">
            <div className="flex items-start gap-3">
              <div className="rounded-full border border-sky-200 bg-white p-2 dark:border-sky-400/20 dark:bg-black/20">
                <Sparkles className="h-4 w-4 text-sky-700 dark:text-sky-200" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-200">
                  Suggested Metadata
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                  {suggestions.rankReason}
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-900 dark:text-white">Lane</label>
                <Select value={formState.workspace} onValueChange={(value) => updateSuggestedField('workspace', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose lane" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUICK_ADD_WORKSPACE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-900 dark:text-white">Category</label>
                <Select value={formState.category} onValueChange={(value) => updateSuggestedField('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose category" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUICK_ADD_CATEGORY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-900 dark:text-white">Urgency</label>
                <Select value={formState.urgency} onValueChange={(value) => updateSuggestedField('urgency', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUICK_ADD_URGENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-900 dark:text-white">Timing</label>
                <Select value={formState.timing} onValueChange={(value) => updateSuggestedField('timing', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose timing" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUICK_ADD_TIMING_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2 md:col-span-2">
                <label className="text-sm font-medium text-slate-900 dark:text-white">Queue Position</label>
                <Select value={formState.position} onValueChange={(value) => updateSuggestedField('position', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose queue position" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUICK_ADD_POSITION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave || creating} className="bg-aqua text-charcoal hover:bg-aqua-dark">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Add to priority board
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
